import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

const FEEDBACK_MS = 2000;

interface Props {
  /** Texto que vai para a área de transferência. */
  value: string;
  /** Rótulo acessível. Ex.: "Copiar link do cardápio". */
  label: string;
  /** Quando true, mostra só o ícone (uso em tabelas). */
  iconOnly?: boolean;
}

/**
 * Copiar-para-área-de-transferência com confirmação visível.
 *
 * Não renderiza nada quando `navigator.clipboard` não existe (contexto não
 * seguro, ex.: HTTP em rede interna): um botão que falha em silêncio é pior que
 * botão nenhum — o link ao lado continua clicável e selecionável.
 */
export function CopyButton({ value, label, iconOnly = false }: Props) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    return null;
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), FEEDBACK_MS);
    } catch {
      // Permissão negada pelo navegador: mantém o estado inalterado em vez de
      // anunciar uma cópia que não aconteceu.
      setCopied(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => void copy()}
        aria-label={label}
        title={label}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
        {!iconOnly && (copied ? 'Copiado!' : 'Copiar')}
      </button>
      {/* Leitor de tela: o troca-ícone sozinho não anuncia nada. */}
      <span aria-live="polite" className="sr-only">
        {copied ? `${label}: copiado` : ''}
      </span>
    </>
  );
}
